/**
 * Reviews / 书友晒单 data.
 *
 * Each entry is an original editorial summary of a third-party blog review,
 * written in our own voice. We embed only a very short attributed pull-quote
 * (well within fair use) plus a prominent link back to the original article.
 *
 * Do NOT paste long passages from the source articles here.
 */

const STATIC = 'https://static.ipastbook.com/assets/images'
const SELF = '/2.0/static/images/reviews'

// Hero images are the original article hero images, re-hosted on our own
// path so we don't depend on third-party server speed or hotlink behaviour
// (editorial fair use, takedown-on-notice). The original source URL is
// captured in each entry's `sourceUrl` for attribution.

const reviews = [
  {
    slug: 'precious-memorial-album',
    sourceUrl: 'https://gogogowithhim.com/ipastbook/',
    sourceBlog: 'Go Go Go With Him 童你去玩',
    sourceAuthor: 'Loretta',
    sourceDate: '2021-01',
    hero: `${SELF}/precious-memorial-album.jpg`,
    heroFallback: `${STATIC}/A5-1.jpg`,
    pullQuote: {
      en: 'Precious album. Priceless memories.',
      'zh-tw': '珍貴的紀念冊，無價的回憶',
      'zh-hk': '珍貴嘅紀念冊，無價嘅回憶'
    },
    locales: {
      en: {
        title: 'Class outings and family milestones, bound into one keepsake',
        teaser: 'A long-time iPastBook fan revisits why she keeps coming back — gifts, class albums and family trips, all in soft-cover photo books.',
        intro: 'Family-travel blogger Loretta has been building iPastBook volumes for years. In her latest write-up she walks through several books she has made for different purposes — a class commemorative album for her child, a family outing volume, and personal milestones — and explains why the format keeps working for her household.',
        sections: [
          {
            heading: 'Why she comes back',
            body: 'What stands out in Loretta\'s review is that she keeps choosing iPastBook for very different occasions. The soft-cover format keeps the per-book cost low enough that gifting becomes practical — she calls it one of the most meaningful gifts she has given.'
          },
          {
            heading: 'What worked for her',
            body: 'Three things did the heavy lifting: a single Facebook login meant no fresh account setup; live chat support helped when she had questions mid-edit; and 50-plus-page volumes turned out to be the sweet spot — long enough to tell a real story without overwhelming the reader.'
          },
          {
            heading: 'Our take',
            body: 'Loretta\'s pattern is one we see often: people start with a single trip or milestone, then realize the same workflow scales to class albums, gifts and yearly recaps. The friction of moving content from social media to print is what kills most photo-book projects, so removing that friction is what brings people back.'
          }
        ]
      },
      'zh-tw': {
        title: '同學會、家庭出遊、個人里程碑，全部收進一本實體相冊',
        teaser: '一位多年回購的書友，分享她為什麼一次又一次回到 iPastBook —— 班級紀念冊、家庭旅行、送禮，全部用軟皮相片書搞定。',
        intro: '家庭旅遊部落客 Loretta 多年來陸續做了好幾本 iPastBook。這次的分享文裡，她整理了自己為不同場合做過的相片書 —— 給孩子的班級紀念冊、家庭出遊的旅行紀錄、個人成長的小里程碑 —— 並回顧為什麼這個流程能持續為她家所用。',
        sections: [
          {
            heading: '為什麼她一直回購',
            body: 'Loretta 的分享裡最有意思的一點，是她在完全不同的場合都會選 iPastBook。軟皮版的單本成本壓得夠低，讓「拿來當禮物」變成可行的選項 —— 她在文章裡形容這是她送過最有意義的禮物之一。'
          },
          {
            heading: '對她而言真正有用的部分',
            body: '三個重點：一是 Facebook 一鍵登入，省去重新註冊；二是邊編輯邊有真人客服可以問；三是 50 頁以上的厚度剛剛好 —— 足以講完一個故事，又不會厚到讓人讀不下去。'
          },
          {
            heading: '我們的觀察',
            body: 'Loretta 的軌跡其實很常見：一開始只是想做一本旅行紀錄，後來發現同樣一套流程也能用在班級紀念冊、節日送禮、年度回顧。多數相片書專案會半途而廢，常常輸在「把社群媒體內容搬到實體書」這一段太麻煩；把這段做順了，人們就會一回再回。'
          }
        ]
      },
      'zh-hk': {
        title: '同學會、家庭出遊、個人里程碑，全部裝訂成一本實體相冊',
        teaser: '一位回購多年的書友，分享佢點解一次又一次返到 iPastBook —— 班級紀念冊、家庭旅行、送禮，全部用軟皮相片書搞掂。',
        intro: '家庭旅遊博客 Loretta 多年嚟陸續整咗幾本 iPastBook。今次嘅分享文，佢將自己為唔同場合整過嘅相片書一次過整理 —— 畀小朋友嘅班級紀念冊、屋企人去旅行嘅遊記、個人成長嘅小里程碑 —— 並回顧呢套做法對佢屋企點解咁好用。',
        sections: [
          {
            heading: '佢點解一直回購',
            body: 'Loretta 嘅分享最有趣嗰點，係佢喺完全唔同嘅場合都揀 iPastBook。軟皮版單本成本壓得夠低，令「攞嚟送禮」變成可行 —— 佢喺文中形容呢樣係佢送過最有意義嘅禮物之一。'
          },
          {
            heading: '對佢嚟講真正有用嗰啲',
            body: '三個重點：一係 Facebook 一鍵登入，唔使重新註冊；二係改緊嗰陣有真人客服可以問；三係 50 頁以上嘅厚度啱啱好 —— 夠講完一個故事，又唔會厚到睇唔晒。'
          },
          {
            heading: '我哋睇法',
            body: 'Loretta 嘅軌跡其實好普遍：一開始可能只係想整本旅行記錄，跟住發覺同一套流程都用得喺班級紀念冊、節日禮物、年度回顧。多數相片書計劃最後做唔成，多數係衰咗喺「將社交媒體內容搬入實體書」呢段太麻煩；呢段順咗，人哋就會一次又一次返嚟。'
          }
        ]
      }
    }
  },
  {
    slug: 'disneyland-family-photobook',
    sourceUrl: 'https://happymommy.pixnet.net/blog/posts/7119995338',
    sourceBlog: 'Wenwen小姐和寶貝們 (PIXNET)',
    sourceAuthor: 'Wenwen',
    sourceDate: '2019-10',
    hero: `${SELF}/disneyland-family-photobook.jpg`,
    heroFallback: `${STATIC}/A4-1.jpg`,
    pullQuote: {
      en: 'The finished book really has substance.',
      'zh-tw': '成品好有質感',
      'zh-hk': '成品好有質感'
    },
    locales: {
      en: {
        title: 'A 41-page Hong Kong Disneyland family trip, in print',
        teaser: 'A Taiwanese parenting blogger turned her August Disneyland trip with the kids into a fabric-cover hardcover photo book — and explains why the finished object surprised her.',
        intro: 'Taiwanese parenting blogger Wenwen documented an August trip to Hong Kong Disneyland with her children, then turned it into a 41-page hardcover photobook through iPastBook. Her review focuses less on the trip and more on the unboxing — what the printed object actually felt like when it arrived.',
        sections: [
          {
            heading: 'What she made',
            body: 'A 41-page hardcover photo book with a fabric cover and a die-cut window frame on the front. The build is the same one a lot of customers default to for trip recaps: heavy enough to live on a coffee table, simple enough that kids will actually flip through it.'
          },
          {
            heading: 'What stood out in her review',
            body: 'Two practical points. First, she edited the book on both her phone and her computer without friction — the same project, two devices. Second, she pulled photos directly from Facebook and Instagram rather than re-uploading them, which is the difference between a half-finished project and a finished book.'
          },
          {
            heading: 'Our take',
            body: 'Trip recaps are one of the most common reasons people start a photo book and one of the most common reasons they abandon one. The thing that closes the gap is almost always the same: not having to think about where the photos live. If your trip is already on Instagram, that should be enough.'
          }
        ]
      },
      'zh-tw': {
        title: '一趟香港迪士尼親子遊，做成 41 頁布面精裝相片書',
        teaser: '台灣親子部落客把八月帶孩子去香港迪士尼的回憶，做成一本布面硬精裝相片書 —— 並分享拆封時為什麼讓她意外。',
        intro: '台灣親子部落客 Wenwen 把八月帶孩子去香港迪士尼的旅程，做成一本 41 頁的硬精裝相片書。整篇分享的重點其實不在旅行本身，而在「實體成品拿到手時的感受」這件事。',
        sections: [
          {
            heading: '她做了什麼',
            body: '一本 41 頁布面硬精裝相片書，封面有一塊鏤空開窗。這款規格是很多人做旅行紀錄時的預設選擇 —— 夠厚實，可以放在客廳茶几上；又夠簡單，小朋友會願意自己翻。'
          },
          {
            heading: '她分享文裡最有感的部分',
            body: '兩個實際的點：第一，同一本書她在手機和電腦上交替編輯都沒卡關；第二，照片直接從 Facebook 和 Instagram 拉過來，不必再重新上傳一遍 —— 這往往就是「做到一半放棄」和「真的做完一本」的差距。'
          },
          {
            heading: '我們的觀察',
            body: '旅行回憶是大家最常想做相片書的場合，也是最常半途而廢的場合。能順利做完的關鍵幾乎都一樣：不用煩惱照片放在哪裡。如果旅程的內容本來就在 Instagram 上，那就應該夠了。'
          }
        ]
      },
      'zh-hk': {
        title: '一次香港迪士尼親子遊，整成 41 頁布面精裝相片書',
        teaser: '台灣親子博客將八月帶小朋友去香港迪士尼嘅回憶，整咗一本布面硬精裝相片書 —— 分享拆封嗰陣點解咁意外。',
        intro: '台灣親子博客 Wenwen 將八月帶小朋友去香港迪士尼嘅行程，做成一本 41 頁嘅硬精裝相片書。佢嘅分享重點唔係旅行本身，而係「實物拎上手嗰下感覺」。',
        sections: [
          {
            heading: '佢做咗啲乜',
            body: '一本 41 頁布面硬精裝相片書，封面有開窗。呢個規格係好多人做旅行紀錄嘅 default —— 夠厚實，可以擺喺客廳茶几；又夠簡單，小朋友會自己揭。'
          },
          {
            heading: '佢分享文最有感嗰段',
            body: '兩個實際嘅點：第一，同一本書佢喺手機同電腦交替編輯都冇 hang 機；第二，相片直接由 Facebook 同 Instagram 抽過嚟，唔使重新上傳 —— 呢個往往就係「做到一半放棄」同「真係做完一本」嘅分別。'
          },
          {
            heading: '我哋睇法',
            body: '旅行回憶係大家最想整相片書嘅場合，亦係最常半途而廢嘅場合。能夠順利做完，關鍵幾乎都係一樣：唔使諗相片放喺邊。如果你嘅行程內容本來就喺 Instagram，咁應該夠喇。'
          }
        ]
      }
    }
  },
  {
    slug: 'yearly-self-gift-photobook',
    sourceUrl: 'https://sunny230.pixnet.net/blog/post/466542017',
    sourceBlog: '酒窩小姊Sunny晴天的角落 (PIXNET)',
    sourceAuthor: 'Sunny',
    sourceDate: '2018-12',
    hero: `${SELF}/yearly-self-gift-photobook.jpg`,
    heroFallback: `${STATIC}/A5-1.jpg`,
    pullQuote: {
      en: 'A book worth making every year.',
      'zh-tw': '一年有必做的一本書',
      'zh-hk': '一年必做嘅一本書'
    },
    locales: {
      en: {
        title: 'A year on Facebook, given to yourself as a year-end gift',
        teaser: 'A Taiwanese blogger frames her annual iPastBook as a present to herself — twelve months of Facebook compressed into one hardcover, made once a year on principle.',
        intro: 'The blogger behind 酒窩小姊Sunny used iPastBook to turn a full year of Facebook posts into a single hardcover photo diary, then framed the whole thing as a year-end gift to herself. She liked it enough to make a second volume the same week, and committed to making one every year.',
        sections: [
          {
            heading: 'A new yearly tradition',
            body: 'The interesting framing here is not the book — it is that she made it a recurring ritual. Year-end self-gifting is a category most photo-book companies underplay because they default to family or partner gifts. Sunny treats the printed book as her personal year-in-review, the way other people might keep a planner.'
          },
          {
            heading: 'What "one-click" actually means in this case',
            body: 'Most products that claim a one-click anything turn out to require many. What stood out in her write-up was that pulling twelve months of posts genuinely stayed close to one click — the cover, the curation, the export all simple enough that she immediately did it a second time. That repeat is the real review.'
          },
          {
            heading: 'Why the year-end framing matters',
            body: 'December is the month people are already in recap mode — calendar resets, gift-giving, the photo recaps every platform auto-generates. A printed book competing for that attention slot wins on one specific axis: permanence. The Reels recap is gone in February. The book is still on the shelf in five years.'
          }
        ]
      },
      'zh-tw': {
        title: '一整年的臉書，當作年終禮物送給自己',
        teaser: '一位台灣部落客把每年一本 iPastBook 當作給自己的年終禮物 —— 12 個月的 Facebook 壓成一本精裝書，原則上每年一本。',
        intro: '部落客 酒窩小姊Sunny 用 iPastBook 把整整一年的 Facebook 貼文做成一本硬精裝相片日記，並把這件事直接定義成「給自己的年終禮物」。她做完一本就立刻又做了一本，並決定每年都做一本下去。',
        sections: [
          {
            heading: '每年一次的儀式',
            body: '這篇分享真正有意思的地方不在書本身，而是她決定把它變成例行儀式。年終送自己一份禮物，是一般相片書品牌很少主動推的場景 —— 大家預設都在說「送家人、送伴侶、送孩子」。Sunny 把這本書當成自己的年度回顧，跟別人寫年度日誌是同一個角色。'
          },
          {
            heading: '所謂「一鍵」到底是什麼樣的一鍵',
            body: '多數聲稱「一鍵」的產品實際操作下來都不只一鍵。她文章裡讓人印象深的，是 12 個月貼文這個體量真的還能維持簡單 —— 封面、篩選、輸出都順到她當下就再做了第二本。重複做這個動作本身就是最強的評價。'
          },
          {
            heading: '為什麼年終這個切角有意義',
            body: '12 月本來就是大家進入「回顧模式」的時段 —— 跨年、送禮、每個平台都會自動生成年度回顧。在這個注意力高峰做一本實體書，能贏的點只有一個：永久。Reels 年度回顧到二月就被刷走了，書五年後還在櫃子裡。'
          }
        ]
      },
      'zh-hk': {
        title: '一整年嘅 Facebook，當作年終禮物送畀自己',
        teaser: '一位台灣博客將每年一本 iPastBook 當作畀自己嘅年終禮物 —— 12 個月嘅 Facebook 壓成一本精裝書，原則上一年一本。',
        intro: '博客 酒窩小姊Sunny 用 iPastBook 將成年嘅 Facebook 貼文整成一本硬精裝相片日記，並將件事直接定義成「畀自己嘅年終禮物」。佢做完一本即刻又做咗一本，並決定每年都做一本落去。',
        sections: [
          {
            heading: '每年一次嘅儀式',
            body: '呢篇分享真正有意思嘅地方唔係本書本身，而係佢決定將佢變成例行儀式。年終送畀自己一份禮物，係一般相片書品牌好少主動推嘅場景 —— 大家預設都係「送畀屋企人、伴侶、小朋友」。Sunny 將呢本書當作自己嘅年度回顧，同其他人寫年度日誌係同一個角色。'
          },
          {
            heading: '所謂「一鍵」到底係咩樣嘅一鍵',
            body: '多數聲稱「一鍵」嘅產品實際做起上嚟都唔止一鍵。佢文章畀我印象深嘅，係 12 個月貼文呢個份量真係仲可以保持簡單 —— 封面、揀內容、輸出全部順到佢當下就做埋第二本。重複做呢個動作本身已經係最強嘅評價。'
          },
          {
            heading: '點解年終呢個切角有意義',
            body: '12 月本身就係大家進入「回顧模式」嘅月份 —— 跨年、送禮、每個 platform 都自動整年度回顧。喺呢個注意力高峰整一本實體書，能贏嘅點只有一個：永久。Reels 年度回顧到二月就畀人 scroll 走咗，書五年後仲喺櫃入面。'
          }
        ]
      }
    }
  },
  {
    slug: 'baby-first-six-months-album',
    sourceUrl: 'https://ianbaby0509.pixnet.net/blog/post/267298932',
    sourceBlog: '簡羿寶寶Ian (PIXNET)',
    sourceAuthor: '簡羿媽',
    sourceDate: '2018-12',
    hero: `${SELF}/baby-first-six-months-album.jpg`,
    heroFallback: `${STATIC}/A4-1.jpg`,
    pullQuote: {
      en: "Baby's first growth record book.",
      'zh-tw': '送給寶寶的第一份成長書',
      'zh-hk': '送畀寶寶嘅第一份成長書'
    },
    locales: {
      en: {
        title: "A 106-page hardcover of a baby's first six months",
        teaser: 'A new mother turned six months of Facebook posts about her newborn into a 106-page hardcover diary — and committed to making a fresh volume every half year.',
        intro: 'The blogger behind 簡羿寶寶Ian made a 106-page hardcover photo diary covering her son\'s first six months, sourced entirely from Facebook posts. She frames it as the first growth-record book she has given her child, and resolved to make a new volume every six months.',
        sections: [
          {
            heading: 'What she made',
            body: 'A 106-page hardcover with a ribbon bookmark, drawn from six months of Facebook posts about her newborn. The volume size matters — past 100 pages it feels like a real photo book rather than a brochure, and a six-month cadence keeps each volume manageable instead of overwhelming.'
          },
          {
            heading: 'Why a digital archive does not replace this for new parents',
            body: 'First-baby content on social media is unusually dense — multiple posts a day in the early weeks. What new parents actually want, six months in, is a way to see all of it at once without scrolling. A printed book inverts the constraint: instead of scrolling through hundreds of posts to find a moment, you flip and stop.'
          },
          {
            heading: 'The six-month cadence trick',
            body: 'Six months is short enough that the milestones are still vivid, and long enough that you have accumulated enough material to fill a real book. A lot of "do it every year" projects stall because a year of newborn content is overwhelming to curate. Half-year volumes solve that, and her commitment to keep making them suggests it is the right rhythm for this stage of family life.'
          }
        ]
      },
      'zh-tw': {
        title: '寶寶 0–6 個月，一本 106 頁的精裝成長紀錄',
        teaser: '一位新手媽媽把寶寶頭半年的 Facebook 貼文做成 106 頁硬精裝日記，並決定每半年都做一本。',
        intro: '部落客 簡羿寶寶Ian 用 iPastBook 把兒子前六個月的 Facebook 貼文做成一本 106 頁的硬精裝相片日記，並把它定位成「送給寶寶的第一份成長記錄書」，同時決定每半年都做新的一本下去。',
        sections: [
          {
            heading: '她做了什麼',
            body: '一本 106 頁、附書籤帶的硬精裝相片書，內容全來自寶寶頭六個月的 Facebook 貼文。書的厚度有意義 —— 過了 100 頁，書就會像本「真正的相片書」而不是冊子；而半年一本的節奏，把每本的份量壓在可控範圍內。'
          },
          {
            heading: '為什麼數位存檔取代不了實體書（對新手父母來說）',
            body: '第一胎在社群上的內容密度異常高 —— 頭幾週每天好幾則貼文。六個月過去，新手父母真正想要的是「一次看完所有片段」這件事，不用滑來滑去。實體書翻轉了這個摩擦：不是滑半天才找到某個瞬間，而是翻一翻就停在那一頁。'
          },
          {
            heading: '半年一本，是個聰明的節奏',
            body: '半年的時間，里程碑還在記憶裡很清楚，但累積的內容已經夠厚到撐起一本書。很多「每年做一本」的計劃最後都做不完，是因為一年的新生兒內容太多、太難挑。半年的份量剛剛好 —— 而她願意一直做下去本身就是評語。'
          }
        ]
      },
      'zh-hk': {
        title: 'BB 0–6 個月，一本 106 頁嘅精裝成長紀錄',
        teaser: '一位新手媽媽將 BB 頭半年嘅 Facebook 貼文整成 106 頁硬精裝日記，並決定每半年都整一本。',
        intro: '博客 簡羿寶寶Ian 用 iPastBook 將仔仔頭六個月嘅 Facebook 貼文整成一本 106 頁硬精裝相片日記，並將佢定位成「送畀 BB 嘅第一份成長記錄書」，同時決定每半年都整一本新嘅落去。',
        sections: [
          {
            heading: '佢做咗啲乜',
            body: '一本 106 頁、有書籤帶嘅硬精裝相片書，內容全部嚟自 BB 頭六個月嘅 Facebook 貼文。書嘅厚度有意義 —— 過 100 頁，本書就會似一本「真嘅相片書」而唔係一本冊子；而半年一本嘅節奏，就將每本嘅份量控制喺啱啱好。'
          },
          {
            heading: '點解 digital archive 唔可以取代實體書（對新手父母嚟講）',
            body: '第一胎喺社交媒體上嘅內容密度高到誇張 —— 頭幾個禮拜一日好幾條 post。半年之後，新手父母真正想要嘅就係「一次過睇晒所有片段」，唔使 scroll 嚟 scroll 去。實體書反轉咗呢個 friction：唔係 scroll 半個鐘揾返某一刻，而係揭一揭就停喺嗰一頁。'
          },
          {
            heading: '半年一本，係個聰明嘅節奏',
            body: '半年呢個時間，啲里程碑仲記得好清楚，但累積落嘅內容已經夠厚去撐起一本書。好多「一年做一本」嘅計劃最後做唔成，係因為一年嘅新生兒內容太多、太難揀。半年呢個份量啱啱好 —— 而佢肯一直繼續做落去，本身已經係最好嘅評語。'
          }
        ]
      }
    }
  },
  {
    slug: 'baby-first-year-keepsake',
    sourceUrl: 'https://sybs.pixnet.net/blog/post/32718643',
    sourceBlog: "Syb's Blah Blah Blog (PIXNET / FashionGuide)",
    sourceAuthor: 'Syb',
    sourceDate: '2019-03',
    hero: `${SELF}/baby-first-year-keepsake.jpg`,
    heroFallback: `${STATIC}/A5-1.jpg`,
    pullQuote: {
      en: 'Warmer than a feed.',
      'zh-tw': '比動態更有溫度',
      'zh-hk': '比動態更有溫度'
    },
    locales: {
      en: {
        title: "Closing the first chapter — a baby's first year as a 50-page hardcover",
        teaser: 'A blogger turned one full year of Facebook posts about her son into a 50-page hardcover, framed as the closing chapter of his first year.',
        intro: "The blogger behind Syb's Blah Blah Blog — whose write-up was syndicated to FashionGuide — used iPastBook to compile her son's first year of Facebook posts into a 50-page hardcover. The framing isn't about volume or workflow; it's about giving the first year a closed chapter that lives outside the feed.",
        sections: [
          {
            heading: 'What she made',
            body: "A 50-page hardcover, sourced from twelve months of Facebook posts about her son's first year. The page count signals a deliberate choice — long enough to feel substantial, short enough that every page earned its spot. Year-one content tends to balloon if you let it; her curation kept it tight."
          },
          {
            heading: 'Why year one is the right milestone to print',
            body: 'First-year photos are the densest social-media content a family ever produces. By month twelve, parents already feel the weight of all that material sitting in cloud archives — and the question becomes which of those moments deserve to outlive the feed. Closing the first year with a book turns the chapter into something you can hand back to the child later.'
          },
          {
            heading: 'The temperature argument',
            body: 'Her core point is one we see often but rarely articulated this clearly: a printed book has more warmth than a feed. Not because the photos are better, but because the act of flipping pages with someone is fundamentally different from each person scrolling on their own phone. The book is shared in a way the archive cannot be.'
          }
        ]
      },
      'zh-tw': {
        title: '把寶寶的第一年收尾 —— 一本 50 頁的精裝紀念冊',
        teaser: '一位部落客把兒子第一年的 Facebook 貼文整理成 50 頁硬精裝，當作這一年的「收尾」。',
        intro: "部落客 Syb's Blah Blah Blog（本文同時刊在 FashionGuide 部落格平台）用 iPastBook 把兒子第一年的 Facebook 貼文做成一本 50 頁的硬精裝。她整篇文章的核心不是工作流也不是頁數，而是「給寶寶第一年一個離開動態牆、走進實體生活的句點」。",
        sections: [
          {
            heading: '她做了什麼',
            body: '一本 50 頁的硬精裝，內容來自兒子第一年的 12 個月 Facebook 貼文。頁數本身是個刻意的選擇 —— 夠厚到拿在手裡是「一本書」，又不會厚到稀釋每一頁。寶寶第一年的素材如果不篩，很容易爆量，她把它壓在剛剛好的份量。'
          },
          {
            heading: '為什麼「第一年」是值得印的里程碑',
            body: '第一年的照片大概是一個家庭一輩子發在社群上密度最高的時段。到第 12 個月，父母自己就感覺得到雲端裡那堆內容的重量 —— 問題變成「哪些片段值得活得比動態牆更久」。把第一年用一本書收尾，等於把這個章節變成日後可以親手交給孩子的東西。'
          },
          {
            heading: '「溫度」這個切角',
            body: '她講出了一件大家常感覺到、但很少有人講清楚的事：實體書比動態牆更有溫度。不是因為照片更好看，而是「翻書這件事可以兩個人一起做」 —— 動態牆本質上是每人滑自己的手機。一本書能被「一起翻」，雲端相簿做不到這件事。'
          }
        ]
      },
      'zh-hk': {
        title: '畀 BB 嘅第一年收尾 —— 一本 50 頁嘅精裝紀念冊',
        teaser: '一位博客將仔仔第一年嘅 Facebook 貼文整理成 50 頁硬精裝，當作呢一年嘅「收尾」。',
        intro: "博客 Syb's Blah Blah Blog（呢篇文同時刊喺 FashionGuide 嘅博客平台）用 iPastBook 將仔仔第一年嘅 Facebook 貼文整成一本 50 頁嘅硬精裝。佢成篇文嘅重點唔係工作流程都唔係頁數，而係「畀 BB 嘅第一年一個離開動態牆、走入實體生活嘅句點」。",
        sections: [
          {
            heading: '佢做咗啲乜',
            body: '一本 50 頁嘅硬精裝，內容嚟自仔仔第一年嘅 12 個月 Facebook 貼文。頁數本身係個刻意嘅選擇 —— 夠厚到攞上手係「一本書」，但又唔會厚到稀釋每一頁。BB 第一年嘅素材唔篩好容易爆量，佢將佢壓喺啱啱好嘅份量。'
          },
          {
            heading: '點解「第一年」係值得印嘅里程碑',
            body: 'BB 第一年嘅相，大概係一個家庭一世發喺社交媒體上密度最高嘅時段。到第 12 個月，父母自己已經感覺到雲端嗰堆內容嘅重量 —— 問題變成「邊啲片段值得活得比動態牆更耐」。將第一年用一本書收尾，等於將呢個 chapter 變成日後可以親手交畀小朋友嘅嘢。'
          },
          {
            heading: '「溫度」呢個切角',
            body: '佢講出咗一樣大家經常感受到、但少有人講得清楚嘅嘢：實體書比動態牆有溫度。唔係因為相靚啲，而係「揭書呢樣嘢可以兩個人一齊做」—— 動態牆本質上係每個人 scroll 自己部手機。一本書可以「一齊揭」，雲端相簿做唔到呢樣嘢。'
          }
        ]
      }
    }
  },
  {
    slug: 'twins-second-birthday-album',
    sourceUrl: 'https://nicco0213.pixnet.net/blog/post/466421675',
    sourceBlog: '厚片公主雙打日記 (PIXNET)',
    sourceAuthor: '厚片公主',
    sourceDate: '2018-12',
    hero: `${SELF}/twins-second-birthday-album.jpg`,
    heroFallback: `${STATIC}/A5-2.jpg`,
    pullQuote: {
      en: 'Memories you can hold.',
      'zh-tw': '握在手中的回憶',
      'zh-hk': '揸喺手裏嘅回憶'
    },
    locales: {
      en: {
        title: 'Twins, from birth to two — a hardcover assembled month by month',
        teaser: 'A twin mother turned two years of Facebook posts about her twin daughters into a hardcover photo book — built month by month — as a 2nd-birthday keepsake.',
        intro: 'The blogger behind 厚片公主雙打日記 used iPastBook to compile two years of Facebook posts about her twin daughters into a single hardcover, arriving in time for their second birthday. Her write-up emphasizes the by-month organization and the simple fact that a physical object lets you hold the memory in a way a phone gallery cannot.',
        sections: [
          {
            heading: 'Why twin parents need this differently',
            body: 'Twin households produce roughly double the social-media content of a single-child household, and the noise-to-signal ratio gets worse fast. Curating it into one cohesive book is a harder job than it is for single-child families — which is exactly why landing on a clean by-month structure matters: the chronology does the curation work for you.'
          },
          {
            heading: 'Month-by-month as a built-in narrative',
            body: 'iPastBook organizes the imported Facebook content by date, which sounds boring until you see what it does for twin content. Two children growing in parallel makes the milestone timeline genuinely useful — "this is what they looked like at five months, here is six months" — and the month dividers turn the book into a flippable timeline instead of a scrapbook.'
          },
          {
            heading: 'Why second birthday is a good print moment',
            body: 'First birthday gets all the attention, but second birthday is often when parents finally have enough of a rhythm to compile something properly. The toddler stage starts to settle, the early-baby noise has filtered out, and 24 months of content is enough to fill a real book without overflowing it. The blogger\'s instinct to print at this moment is one we see repeated across our customer base.'
          }
        ]
      },
      'zh-tw': {
        title: '雙胞胎 0–2 歲，一本按月份排列的精裝紀念冊',
        teaser: '一位雙胞胎媽媽把女兒們兩年的 Facebook 貼文做成一本按月份分章的硬精裝，準備當作 2 歲生日的禮物。',
        intro: '部落客 厚片公主雙打日記 用 iPastBook 把雙胞胎女兒從出生到接近 2 歲的 Facebook 貼文整理成一本硬精裝，預計趕在她們 2 歲生日當禮物。她整篇文章的重點不在工作量，而在「按月份分章」這個結構，以及「實體書可以握在手中」這件事。',
        sections: [
          {
            heading: '為什麼雙胞胎家庭的需求不一樣',
            body: '雙胞胎家庭發在社群上的內容差不多是一胎家庭的兩倍，雜訊也跟著放大。要把兩年的素材整成一本連貫的書，本來就比單胎難 —— 所以「按月份排序」這個結構就特別關鍵：讓時間順序自己把篩選工作做完。'
          },
          {
            heading: '按月分章 = 內建敘事',
            body: 'iPastBook 預設把匯入的 Facebook 內容按時間排序，這聽起來很普通，但對雙胞胎內容來說很有用。兩個孩子一起長大，里程碑時間軸本身就好看 —— 「這是她們 5 個月的樣子，這是 6 個月」—— 月份分隔頁讓整本書變成一條可以一頁一頁翻過去的時間軸，而不是無結構的剪貼簿。'
          },
          {
            heading: '為什麼「2 歲生日」是個好的印刷時機',
            body: '1 歲生日最多人慶祝，但 2 歲生日才是父母真的有節奏感、可以把素材整理完整的時候。幼兒期開始穩定，新生兒階段的雜訊已經沉澱，24 個月的內容剛好能撐起一本不會爆量的書。她選擇在這個時間點印一本書，是我們在很多客戶身上看到的同一個直覺。'
          }
        ]
      },
      'zh-hk': {
        title: '雙胞胎 0–2 歲，一本按月份分章嘅精裝紀念冊',
        teaser: '一位雙胞胎媽媽將女女兩年嘅 Facebook 貼文整成一本按月份分章嘅硬精裝，預備做佢哋 2 歲生日禮物。',
        intro: '博客 厚片公主雙打日記 用 iPastBook 將雙胞胎女女由出生到接近 2 歲嘅 Facebook 貼文整理成一本硬精裝，預備趕得切佢哋 2 歲生日做禮物。佢成篇文嘅重點唔喺工作量，而係「按月份分章」呢個結構，加埋「實體書可以揸喺手裏」呢樣嘢。',
        sections: [
          {
            heading: '點解雙胞胎家庭需求唔同',
            body: '雙胞胎家庭發喺社交媒體上嘅內容大概係一胎家庭嘅兩倍，雜訊都跟住放大。要將兩年嘅素材整成一本連貫嘅書，本身就比單胎難 —— 所以「按月份排序」呢個結構就特別關鍵：等時間順序自己將篩選工作做完。'
          },
          {
            heading: '按月分章 = 內建敘事',
            body: 'iPastBook 預設將匯入嘅 Facebook 內容按時間排好，聽起嚟好普通，但對雙胞胎內容嚟講好有用。兩個小朋友一齊長大，里程碑時間軸本身已經好睇 —— 「呢張係佢哋 5 個月，呢張係 6 個月」—— 月份分隔頁將成本書變成一條可以一頁一頁揭落去嘅時間軸，而唔係無結構嘅剪貼簿。'
          },
          {
            heading: '點解「2 歲生日」係個好嘅印刷時機',
            body: '1 歲生日多人慶祝，但 2 歲生日先係父母真係有節奏感、可以將素材整理完整嘅時間。幼兒期開始穩定，新生兒階段嘅雜訊已經沉澱，24 個月嘅內容啱啱好撐到一本唔會爆量嘅書。佢揀呢個時間點印書，係我哋喺好多客戶身上見到嘅同一個直覺。'
          }
        ]
      }
    }
  },
  {
    slug: 'facebook-printed-memories',
    sourceUrl: 'https://kimfamilytravel.com/2018/06/ipastbook.html',
    sourceBlog: '呀劍萬帥‧親子旅遊‧攝影札記',
    sourceAuthor: 'Kim Family Travel',
    sourceDate: '2018-06',
    hero: `${SELF}/facebook-printed-memories.jpg`,
    heroFallback: `${STATIC}/A5-2.jpg`,
    pullQuote: {
      en: 'Memories you can actually touch.',
      'zh-tw': '觸摸得到的回憶',
      'zh-hk': '摸得到嘅回憶'
    },
    locales: {
      en: {
        title: 'Five years of Facebook posts, bound into one family album',
        teaser: 'A Hong Kong family-travel photographer compiled five years of Facebook memories into a single hardcover book — and the family rediscovered moments together.',
        intro: 'A Hong Kong family-travel and photography blogger compiled roughly five years of Facebook posts — trips, growing-up moments, daily snapshots — into a single hardcover photo book through iPastBook. The review is honest about what the process is and is not.',
        sections: [
          {
            heading: 'What he made',
            body: 'A hardcover photo book pulling together about five years of Facebook content. The hard part of a project like this is usually the curation, not the design — iPastBook\'s default workflow handles the layout automatically and lets the editor focus on which posts deserve a spot.'
          },
          {
            heading: 'Where the review is honest',
            body: 'He notes that Facebook\'s image compression isn\'t ideal for print — and then notes that the finished book still holds up. That trade-off matters: a lot of would-be projects stall over fear that the photos won\'t look good enough. The blogger\'s answer is that for a family keepsake, the photos look the way the memories already feel.'
          },
          {
            heading: 'Our take',
            body: 'The thing that hits hardest in this review is the moment he describes the family sitting together and rediscovering forgotten posts. That\'s the actual product. The book is a delivery mechanism for a shared family moment — and it\'s a moment that doesn\'t happen when the same posts live on a phone.'
          }
        ]
      },
      'zh-tw': {
        title: '五年的 Facebook 貼文，裝訂成一本家庭相冊',
        teaser: '一位香港親子旅遊攝影師，把五年的 Facebook 回憶整理成一本精裝相片書 —— 一家人重新發現那些早就忘了的片段。',
        intro: '一位香港親子旅遊兼攝影部落客，把大約五年份的 Facebook 內容 —— 旅行、孩子的成長片段、日常生活 —— 整理成一本 iPastBook 硬精裝相片書。他的文章對整個過程是很誠實的。',
        sections: [
          {
            heading: '他做了什麼',
            body: '一本硬精裝相片書，內容涵蓋大約五年的 Facebook 貼文。這類專案真正難的不是排版，而是篩選 —— iPastBook 預設的流程已經把版型自動排好，編輯時可以把心思花在「哪幾則貼文值得留下」這件事上。'
          },
          {
            heading: '他文章誠實的部分',
            body: '他直接點出 Facebook 的照片壓縮對於印刷其實不算理想 —— 然後緊接著說：最後的成品還是撐得住。這個取捨很重要，因為很多原本想做的人，是被「照片會不會印壞」這個焦慮卡住的。對於家庭紀念來說，他的答案是：照片看起來，就跟回憶本來的樣子一樣。'
          },
          {
            heading: '我們的觀察',
            body: '這篇分享最打動人的，是他描述一家人坐在一起、重新發現早就忘掉的貼文那一段。這才是真正的產品。書只是一個載體，承載著一段「全家人一起翻一翻」的時刻 —— 而當這些貼文留在手機裡時，這一刻不會發生。'
          }
        ]
      },
      'zh-hk': {
        title: '五年 Facebook 貼文，裝訂成一本屋企相冊',
        teaser: '一位香港親子旅遊攝影師，將五年嘅 Facebook 回憶整成一本精裝相片書 —— 一家人一齊重新發現嗰啲早就唔記得嘅片段。',
        intro: '一位香港親子旅遊兼攝影博客，將大約五年嘅 Facebook 內容 —— 旅行、小朋友成長片段、日常生活 —— 整成一本 iPastBook 硬精裝相片書。佢嘅文章對成個過程都好誠實。',
        sections: [
          {
            heading: '佢做咗啲乜',
            body: '一本硬精裝相片書，內容涵蓋大約五年嘅 Facebook 貼文。呢類 project 真正難嘅唔係排版，而係揀邊啲 post —— iPastBook 預設流程已經自動排好版，可以將心機放喺「邊啲 post 值得留低」呢件事上。'
          },
          {
            heading: '佢文章誠實嗰部分',
            body: '佢直接點出 Facebook 嘅相片壓縮對印刷嚟講其實麻麻 —— 不過跟住即刻講：最後成品都頂得住。呢個取捨好重要，因為好多想做嘅人，都係畀「相片會唔會印衰咗」呢個焦慮卡住。對家庭紀念嚟講，佢嘅答案係：相片睇起嚟，就同回憶本身嘅樣冇兩樣。'
          },
          {
            heading: '我哋睇法',
            body: '呢篇分享最打動人嘅，係佢描述全家人坐埋一齊、重新發現早就唔記得嘅 post 嗰段。呢樣先係真正嘅產品。書只係一個載體，載住一段「一家人一齊揭一揭」嘅時刻 —— 但係呢啲 post 留喺電話入面嘅話，呢一刻就唔會發生。'
          }
        ]
      }
    }
  }
]

export function getReviews () {
  return reviews
}

export function getReview (slug) {
  return reviews.find(r => r.slug === slug)
}

export default reviews
